import { useEffect, useMemo, useState } from 'react';
import {
  buildPrerequisiteOptions,
  formatDateTimeLocal,
  type BuilderItem,
  type BuilderSection,
} from './course-builder-types';

interface CourseBuilderItemSettingsModalProps {
  item: BuilderItem | null;
  sections: BuilderSection[];
  saving?: boolean;
  onClose: () => void;
  onSave: (item: BuilderItem) => void;
}

export function CourseBuilderItemSettingsModal({
  item,
  sections,
  saving = false,
  onClose,
  onSave,
}: CourseBuilderItemSettingsModalProps) {
  const [draft, setDraft] = useState<BuilderItem | null>(item);

  useEffect(() => {
    setDraft(item);
  }, [item]);

  const prerequisiteOptions = useMemo(
    () => (draft ? buildPrerequisiteOptions(sections, draft) : []),
    [draft, sections],
  );

  if (!item || !draft) return null;

  const isLabel = Boolean(draft.isLabel);
  const h5p = draft.h5pInstance;
  const assessment = draft.assessmentInstance;

  function updateDraft(next: BuilderItem) {
    setDraft(next);
  }

  return (
    <div
      className="lms-builder-settings-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="builder-item-settings-title"
    >
      <div className="lms-builder-settings-modal__panel">
        <div className="lms-builder-settings-modal__head">
          <h3 id="builder-item-settings-title" className="lms-builder-settings-modal__title">
            {isLabel ? 'Configuración de la etiqueta' : 'Configuración de la actividad'}
          </h3>
          <button
            type="button"
            className="lms-builder-settings-modal__close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <div className="lms-builder-settings-modal__body">
          <div className="form-field">
            <label className="form-label" htmlFor="builder-item-title">
              {isLabel ? 'Texto de la etiqueta' : 'Título personalizado'}
            </label>
            <input
              id="builder-item-title"
              type="text"
              value={draft.titleOverride ?? draft.displayTitle}
              className={`form-input-base${isLabel ? ' uppercase' : ''}`}
              onChange={(event) => {
                const value = isLabel ? event.target.value.toUpperCase() : event.target.value;
                updateDraft({
                  ...draft,
                  titleOverride: value,
                  displayTitle: value,
                  title: value,
                });
              }}
            />
          </div>

          <label className="lms-builder-settings-modal__checkbox">
            <input
              type="checkbox"
              checked={draft.isVisible !== false}
              onChange={(event) => updateDraft({ ...draft, isVisible: event.target.checked })}
            />
            <span>Visible para estudiantes</span>
          </label>

          {!isLabel && (
            <>
              <div className="lms-builder-settings-modal__section">
                <p className="lms-builder-settings-modal__section-title">Disponibilidad</p>
                <div className="lms-builder-settings-modal__grid">
                  <div className="form-field">
                    <label className="form-label" htmlFor="builder-available-from">
                      Disponible desde
                    </label>
                    <input
                      id="builder-available-from"
                      type="datetime-local"
                      className="form-input-base"
                      value={formatDateTimeLocal(draft.availableFrom)}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          availableFrom: event.target.value || null,
                        })
                      }
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label" htmlFor="builder-available-until">
                      Disponible hasta
                    </label>
                    <input
                      id="builder-available-until"
                      type="datetime-local"
                      className="form-input-base"
                      value={formatDateTimeLocal(draft.availableUntil)}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          availableUntil: event.target.value || null,
                        })
                      }
                    />
                  </div>
                </div>
                <p className="form-hint">Deje en blanco si no aplica límite de fechas.</p>
              </div>

              <div className="lms-builder-settings-modal__section">
                <div className="form-field">
                  <label className="form-label" htmlFor="builder-requires-item">
                    Requiere completar
                  </label>
                  <select
                    id="builder-requires-item"
                    className="form-input-base"
                    value={draft.requiresItemId ? String(draft.requiresItemId) : ''}
                    onChange={(event) =>
                      updateDraft({
                        ...draft,
                        requiresItemId: event.target.value || null,
                      })
                    }
                  >
                    <option value="">Ninguna actividad previa</option>
                    {prerequisiteOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {draft.typeValue === 'h5p' && h5p && (
                <div className="lms-builder-settings-modal__section">
                  <p className="lms-builder-settings-modal__section-title">Configuración H5P</p>
                  <label className="lms-builder-settings-modal__checkbox">
                    <input
                      type="checkbox"
                      checked={h5p.isRequired === true}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          h5pInstance: { ...h5p, isRequired: event.target.checked },
                        })
                      }
                    />
                    <span>Obligatoria</span>
                  </label>
                  <label className="lms-builder-settings-modal__checkbox">
                    <input
                      type="checkbox"
                      checked={h5p.allowPreview !== false}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          h5pInstance: { ...h5p, allowPreview: event.target.checked },
                        })
                      }
                    />
                    <span>Permitir vista previa</span>
                  </label>
                  <label className="lms-builder-settings-modal__checkbox">
                    <input
                      type="checkbox"
                      checked={h5p.unlimitedAttempts !== false}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          h5pInstance: { ...h5p, unlimitedAttempts: event.target.checked },
                        })
                      }
                    />
                    <span>Intentos ilimitados</span>
                  </label>
                  {h5p.unlimitedAttempts === false && (
                    <div className="form-field">
                      <label className="form-label" htmlFor="builder-h5p-max-attempts">
                        Intentos máximos
                      </label>
                      <input
                        id="builder-h5p-max-attempts"
                        type="number"
                        min={1}
                        className="form-input-base"
                        value={h5p.maxAttempts ?? 1}
                        onChange={(event) =>
                          updateDraft({
                            ...draft,
                            h5pInstance: {
                              ...h5p,
                              maxAttempts: Number.parseInt(event.target.value, 10) || 1,
                            },
                          })
                        }
                      />
                    </div>
                  )}
                  <label className="lms-builder-settings-modal__checkbox">
                    <input
                      type="checkbox"
                      checked={h5p.allowRetry !== false}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          h5pInstance: { ...h5p, allowRetry: event.target.checked },
                        })
                      }
                    />
                    <span>Permitir reintentar</span>
                  </label>
                  <label className="lms-builder-settings-modal__checkbox">
                    <input
                      type="checkbox"
                      checked={h5p.showSolutions === true}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          h5pInstance: { ...h5p, showSolutions: event.target.checked },
                        })
                      }
                    />
                    <span>Mostrar soluciones</span>
                  </label>
                  <div className="form-field">
                    <label className="form-label" htmlFor="builder-h5p-grade-weight">
                      Peso en calificación (%)
                    </label>
                    <input
                      id="builder-h5p-grade-weight"
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      className="form-input-base"
                      value={h5p.gradeWeight ?? ''}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          h5pInstance: {
                            ...h5p,
                            gradeWeight: event.target.value ? Number.parseFloat(event.target.value) : null,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              )}

              {draft.typeValue === 'assessment' && assessment && (
                <div className="lms-builder-settings-modal__section">
                  <p className="lms-builder-settings-modal__section-title">Configuración evaluación</p>
                  <label className="lms-builder-settings-modal__checkbox">
                    <input
                      type="checkbox"
                      checked={assessment.isRequired === true}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          assessmentInstance: { ...assessment, isRequired: event.target.checked },
                        })
                      }
                    />
                    <span>Obligatoria</span>
                  </label>
                  <label className="lms-builder-settings-modal__checkbox">
                    <input
                      type="checkbox"
                      checked={assessment.unlimitedAttempts !== false}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          assessmentInstance: { ...assessment, unlimitedAttempts: event.target.checked },
                        })
                      }
                    />
                    <span>Intentos ilimitados</span>
                  </label>
                  {assessment.unlimitedAttempts === false && (
                    <div className="form-field">
                      <label className="form-label" htmlFor="builder-assessment-max-attempts">
                        Intentos máximos
                      </label>
                      <input
                        id="builder-assessment-max-attempts"
                        type="number"
                        min={1}
                        className="form-input-base"
                        value={assessment.maxAttempts ?? 1}
                        onChange={(event) =>
                          updateDraft({
                            ...draft,
                            assessmentInstance: {
                              ...assessment,
                              maxAttempts: Number.parseInt(event.target.value, 10) || 1,
                            },
                          })
                        }
                      />
                    </div>
                  )}
                  <div className="form-field">
                    <label className="form-label" htmlFor="builder-assessment-time-limit">
                      Tiempo límite (min)
                    </label>
                    <input
                      id="builder-assessment-time-limit"
                      type="number"
                      min={0}
                      className="form-input-base"
                      value={assessment.timeLimitMinutes ?? ''}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          assessmentInstance: {
                            ...assessment,
                            timeLimitMinutes: event.target.value ? Number.parseInt(event.target.value, 10) : null,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="lms-builder-settings-modal__grid">
                    <div className="form-field">
                      <label className="form-label" htmlFor="builder-assessment-opens-at">
                        Apertura del intento
                      </label>
                      <input
                        id="builder-assessment-opens-at"
                        type="datetime-local"
                        className="form-input-base"
                        value={formatDateTimeLocal(assessment.opensAt)}
                        onChange={(event) =>
                          updateDraft({
                            ...draft,
                            assessmentInstance: {
                              ...assessment,
                              opensAt: event.target.value || null,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label className="form-label" htmlFor="builder-assessment-closes-at">
                        Cierre del intento
                      </label>
                      <input
                        id="builder-assessment-closes-at"
                        type="datetime-local"
                        className="form-input-base"
                        value={formatDateTimeLocal(assessment.closesAt)}
                        onChange={(event) =>
                          updateDraft({
                            ...draft,
                            assessmentInstance: {
                              ...assessment,
                              closesAt: event.target.value || null,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <p className="form-hint">Fechas opcionales para limitar cuándo se puede iniciar el intento.</p>
                  <div className="form-field">
                    <label className="form-label" htmlFor="builder-assessment-grade-weight">
                      Peso en calificación (%)
                    </label>
                    <input
                      id="builder-assessment-grade-weight"
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      className="form-input-base"
                      value={assessment.gradeWeight ?? ''}
                      onChange={(event) =>
                        updateDraft({
                          ...draft,
                          assessmentInstance: {
                            ...assessment,
                            gradeWeight: event.target.value ? Number.parseFloat(event.target.value) : null,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="lms-builder-settings-modal__footer">
          <button type="button" className="lms-builder-settings-modal__btn-ghost" onClick={onClose}>
            Cerrar
          </button>
          <button
            type="button"
            className="lms-builder-settings-modal__btn-primary"
            onClick={() => draft && onSave(draft)}
            disabled={saving}
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
